import { Component, computed, input, output, signal, viewChildren } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidAssessmentToast } from '../rid-assessment-toast/rid-assessment-toast';
import { RidBottomButton } from '../rid-bottom-button/rid-bottom-button';
import { RidMechanicBackground } from '../rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidSentence } from '../rid-sentence/rid-sentence';
import { RidTopBar } from '../rid-top-bar/rid-top-bar';

@Component({
  selector: 'rid-sentence-reading-base',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidSentence,
    RidAssessmentToast,
    RidBottomButton,
  ],
  templateUrl: './rid-sentence-reading-base.html',
  host: {
    class: 'block size-full',
  },
})
export class RidSentenceReadingBase {
  title = input.required<string>();
  sentencesMatrix = input.required<string[][]>();
  pace = input.required<number>();
  doSentencePass = input.required<(sentence: RidSentence, pace: number) => Promise<void>>();

  sentenceComps = viewChildren<RidSentence>('sentences');
  step = signal(0);
  sentences = computed(() => this.sentencesMatrix()[this.step()]);
  state = signal<{ type: 'STARTING' } | { type: 'READING'; index: number } | { type: 'ASSESSING'; index: number } | { type: 'REVIEWING' }>({
    type: 'STARTING',
  });

  finished = output<void>();

  start() {
    this.pass(0, this.pace());
  }

  async pass(index: number, pace: number) {
    this.state.set({ type: 'READING', index });

    await this.doSentencePass()(this.sentenceComps()[index], pace);

    this.assess(index);
  }

  assess(index: number) {
    this.state.set({ type: 'ASSESSING', index });
  }

  assessed(result: 'CORRECT' | 'INCORRECT') {
    this.nextSentence();
  }

  async nextSentence() {
    const state = this.state();
    if (!('index' in state)) throw new Error('Invalid state');

    const nextIndex = state.index + 1;

    if (nextIndex >= this.sentences().length) {
      this.review();
    } else {
      this.sentenceComps()
        .at(nextIndex - 1)
        ?.hide();

      await this.pass(nextIndex, this.pace());
    }
  }

  review() {
    this.state.set({ type: 'REVIEWING' });
    this.sentenceComps().forEach((comp) => comp.show());
  }

  async nextStory() {
    this.step.update((s) => s + 1);
    this.state.set({ type: 'STARTING' });

    await delay(1000);
    this.start();
  }

  finish() {
    this.finished.emit();
  }
}
